import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import { HOODIE_API_BASE_URL } from "../../api/hoodieClient";
import {
  isMlistApiConfigured,
  isMlistApiValid,
  MLIST_API_BASE_URL,
} from "../../api/mlistClient";
import { getListingOwnerRelationship } from "../../api/services/adminToolsBridgeApi";
import {
  getListingById,
  getTags,
  isHoodieApiConfigured,
  patchListingById,
} from "../../api/services/hoodieApi";
import {
  BLOCKED_ADMIN_FIELDS,
  PATCH_TEXT_FIELDS,
  SERIALIZER_LISTING_FIELDS,
  API_ABSENT_FIELDS,
  buildProductionTextPatch,
  extractTagIds,
  formatApiError,
  isDevMode,
  listingResponseFieldKeys,
  normalizeTagsList,
  pickEditableForm,
  textFieldsDirty,
} from "./listingsWranglerUtils";
import AdminToolsMlistAuthStatus from "./AdminToolsMlistAuthStatus";

const Wrap = styled.div`
  width: 100%;
  padding: 24px 16px;
`;

const TitleRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: end;
  justify-content: space-between;
`;

const Title = styled.h1`
  font-size: 22px;
  margin: 0;
`;

const Sub = styled.div`
  color: #666;
  margin-top: 4px;
  font-size: 13px;
`;

const Card = styled.div`
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  padding: 16px;
  background: #fff;
  margin-top: 12px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 12px;
  color: #444;
`;

const Input = styled.input`
  padding: 8px 10px;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
`;

const TextArea = styled.textarea`
  padding: 8px 10px;
  border: 1px solid #d8d8d8;
  border-radius: 8px;
  min-height: 110px;
`;

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
`;

const Button = styled.button`
  padding: 9px 12px;
  border-radius: 8px;
  border: 1px solid #d0d0d0;
  background: ${(p) => (p.$primary ? "#111" : "#fff")};
  color: ${(p) => (p.$primary ? "#fff" : "#111")};
  cursor: pointer;
  opacity: ${(p) => (p.disabled ? 0.55 : 1)};
`;

const Banner = styled.div`
  border-radius: 10px;
  padding: 10px 12px;
  border: 1px solid
    ${(p) =>
      p.$kind === "error"
        ? "#f1b7b7"
        : p.$kind === "warn"
        ? "#f0e0a8"
        : "#cfe6cf"};
  background: ${(p) =>
    p.$kind === "error"
      ? "#fff5f5"
      : p.$kind === "warn"
      ? "#fffbeb"
      : "#f4fff4"};
  color: ${(p) =>
    p.$kind === "error"
      ? "#7a1c1c"
      : p.$kind === "warn"
      ? "#6b5700"
      : "#1f5f1f"};
  margin-top: 12px;
  font-size: 13px;
`;

const Muted = styled.div`
  color: #666;
  font-size: 13px;
`;

const DevPanel = styled.pre`
  margin: 8px 0 0;
  padding: 10px;
  background: #f7f7f7;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  font-size: 11px;
  overflow: auto;
  max-height: 280px;
`;

const TagChip = styled.span`
  display: inline-block;
  margin: 2px 6px 2px 0;
  padding: 2px 8px;
  background: #eee;
  border-radius: 999px;
  font-size: 12px;
`;

function resolveTagLabels(tagIds, allTags) {
  if (!Array.isArray(tagIds) || !Array.isArray(allTags)) return [];
  return tagIds.map((tid) => {
    const t = allTags.find((x) => x.id === tid);
    return t ? `${t.name} (${t.slug})` : `id:${tid}`;
  });
}

function blockedFieldDisplay(listing, field) {
  if (!Object.prototype.hasOwnProperty.call(listing || {}, field)) {
    if (field === "recommended_by") {
      return "Not on DirectoryEntrySerializer — cannot PATCH via hoodie API";
    }
    return "Not returned by legacy API response";
  }
  const v = listing[field];
  if (v === null || v === undefined || v === "") return "—";
  return String(v);
}

function relationshipMessage(err) {
  const status = err?.status;
  if (!isMlistApiConfigured()) {
    return {
      kind: "warn",
      title: "Ownership bridge unavailable",
      detail: "Set REACT_APP_MLIST_API_BASE_URL to enable owner relationships.",
    };
  }
  if (!isMlistApiValid()) {
    return {
      kind: "warn",
      title: "Invalid MList API base URL",
      detail:
        "Fix REACT_APP_MLIST_API_BASE_URL (single http:// or https:// prefix, no https:https://).",
    };
  }
  if (status === 401 || status === 403) {
    return {
      kind: "warn",
      title: "Staff API auth required",
      detail:
        "This panel is served by mlist-api and requires a staff JWT (Bearer token).",
    };
  }
  if (status === 404) {
    return {
      kind: "warn",
      title: "Ownership bridge unavailable",
      detail:
        "The bridge endpoint returned 404 (disabled or not routed on this backend).",
    };
  }
  return {
    kind: "error",
    title: "Ownership bridge error",
    detail: formatApiError(err),
  };
}

export default function ListingsWranglerDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const devMode = isDevMode();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState(null);
  const [saveErr, setSaveErr] = useState(null);
  const [saveFieldErrors, setSaveFieldErrors] = useState(null);
  const [success, setSuccess] = useState(false);

  const [listing, setListing] = useState(null);
  const [allTags, setAllTags] = useState([]);

  const [relLoading, setRelLoading] = useState(false);
  const [relErr, setRelErr] = useState(null);
  const [relationship, setRelationship] = useState(null);

  const [initialForm, setInitialForm] = useState(null);
  const [form, setForm] = useState(null);

  const [lastPatchRequest, setLastPatchRequest] = useState(null);
  const [lastPatchResult, setLastPatchResult] = useState(null);
  const [lastSavedFieldKeys, setLastSavedFieldKeys] = useState([]);

  const detailEndpoint = `listings/${id}/`;

  async function loadOwnerRelationship(listingId) {
    if (!isMlistApiConfigured() || !isMlistApiValid()) {
      setRelationship(null);
      setRelErr({
        status: 404,
        message: !isMlistApiConfigured()
          ? "mlist-api not configured"
          : "invalid mlist-api base URL",
      });
      setRelLoading(false);
      return;
    }
    setRelLoading(true);
    setRelErr(null);
    try {
      const data = await getListingOwnerRelationship(listingId);
      setRelationship(data);
    } catch (e) {
      setRelationship(null);
      setRelErr(e);
    } finally {
      setRelLoading(false);
    }
  }

  const textDirty = useMemo(
    () => textFieldsDirty(initialForm, form),
    [initialForm, form]
  );

  const pendingPatch = useMemo(() => {
    if (!initialForm || !form) return {};
    return buildProductionTextPatch(initialForm, form);
  }, [initialForm, form]);

  const patchKeyCount = Object.keys(pendingPatch).length;

  const validation = useMemo(() => {
    const issues = [];
    if (!form) return { issues, canSave: false };
    if (!String(form.business_name || "").trim()) {
      issues.push("Business name is required.");
    }
    const ext = String(form.extended_description || "");
    if (ext.length > 2000) {
      issues.push("Extended description exceeds 2000 characters.");
    }
    const short = String(form.short_description || "");
    if (short.length > 100) {
      issues.push("Short description exceeds 100 characters.");
    }
    const canSave =
      issues.length === 0 && textDirty && patchKeyCount > 0;
    return { issues, canSave };
  }, [form, textDirty, patchKeyCount]);

  async function load() {
    if (!isHoodieApiConfigured()) return;
    setLoading(true);
    setErr(null);
    setSuccess(false);
    setSaveFieldErrors(null);
    setSaveErr(null);
    try {
      const [l, tagsRaw] = await Promise.all([
        getListingById(id),
        getTags(),
      ]);
      setListing(l);
      setAllTags(normalizeTagsList(tagsRaw));
      const tagIds = extractTagIds(l?.tags);
      const f = pickEditableForm(l, tagIds);
      setInitialForm(f);
      setForm(f);
      loadOwnerRelationship(l?.id ?? id);
      setLastPatchRequest(null);
      setLastPatchResult({
        type: "load",
        status: 200,
        keys: listingResponseFieldKeys(l),
      });
    } catch (e) {
      setErr(e);
      setListing(null);
      setAllTags([]);
      setInitialForm(null);
      setForm(null);
      setRelationship(null);
      setRelErr(null);
      setLastPatchResult({
        type: "load_error",
        status: e?.status,
        message: formatApiError(e),
      });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleSave() {
    if (!form || !initialForm) return;

    if (patchKeyCount === 0) {
      setSaveErr(null);
      setSuccess(false);
      return;
    }

    setSaving(true);
    setSaveErr(null);
    setSaveFieldErrors(null);
    setSuccess(false);

    const endpoint = `${HOODIE_API_BASE_URL}${detailEndpoint}`;
    const body = { ...pendingPatch };
    setLastPatchRequest({ method: "PATCH", endpoint, body });

    try {
      const { data: updated, status } = await patchListingById(id, body);
      setListing(updated);
      const tagIds = extractTagIds(updated?.tags);
      const next = pickEditableForm(updated, tagIds);
      setInitialForm(next);
      setForm(next);
      setSuccess(true);
      setLastSavedFieldKeys(Object.keys(body));
      setLastPatchResult({
        type: "patch_ok",
        status,
        keys: listingResponseFieldKeys(updated),
        patchKeys: Object.keys(body),
      });
    } catch (e) {
      setSaveErr(e);
      if (e?.data && typeof e.data === "object") {
        setSaveFieldErrors(e.data);
      }
      setLastPatchResult({
        type: "patch_error",
        status: e?.status,
        message: formatApiError(e),
        patchKeys: Object.keys(body),
        data: e?.data,
      });
    } finally {
      setSaving(false);
    }
  }

  if (!isHoodieApiConfigured()) {
    return (
      <Wrap>
        <Title>Listing</Title>
        <Muted>
          Hoodie API is not configured. Set{" "}
          <code>REACT_APP_HOODIE_API_BASE_URL</code>.
        </Muted>
      </Wrap>
    );
  }

  const displayTagIds = form ? extractTagIds(form.tags) : [];
  const tagLabels = resolveTagLabels(displayTagIds, allTags);

  return (
    <Wrap>
      <TitleRow>
        <div>
          <Title>Listing #{id}</Title>
          <Sub>
            Legacy API: <code>{HOODIE_API_BASE_URL}{detailEndpoint}</code>
          </Sub>
        </div>
        <Row>
          <Button type="button" onClick={() => navigate(-1)}>
            Back
          </Button>
          <Button
            type="button"
            disabled={!textDirty || saving || loading}
            onClick={() => {
              setForm(initialForm);
              setSaveErr(null);
              setSaveFieldErrors(null);
              setSuccess(false);
            }}
          >
            Reset
          </Button>
          <Button
            $primary
            type="button"
            disabled={!validation.canSave || saving || loading}
            onClick={handleSave}
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </Row>
      </TitleRow>

      <div style={{ marginTop: 10 }}>
        <Link to="/admin-tools/listings">← Listings</Link>
        <span style={{ color: "#999" }}> • </span>
        <Link to="/admin-tools">Admin Tools</Link>
      </div>

      <AdminToolsMlistAuthStatus />

      {loading ? (
        <Card>
          <em>Loading listing…</em>
        </Card>
      ) : err ? (
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 6 }}>Error</div>
          <div style={{ marginBottom: 6 }}>{formatApiError(err)}</div>
          {err.status ? <div>Status: {err.status}</div> : null}
        </Card>
      ) : !form ? (
        <Card>Unable to load.</Card>
      ) : (
        <>
          {success ? (
            <Banner>
              Saved{lastSavedFieldKeys.length
                ? `: ${lastSavedFieldKeys.join(", ")}`
                : "."}
            </Banner>
          ) : null}

          {!textDirty && !success ? (
            <Banner $kind="warn">
              No editable fields have been changed. Update a text field below,
              then Save.
            </Banner>
          ) : null}

          {saveErr ? (
            <Banner $kind="error">
              <div style={{ fontWeight: 700 }}>Save failed</div>
              <div>{formatApiError(saveErr)}</div>
              {saveErr.status ? <div>HTTP {saveErr.status}</div> : null}
              {saveFieldErrors ? (
                <ul style={{ margin: "8px 0 0", paddingLeft: 18 }}>
                  {Object.entries(saveFieldErrors).map(([field, msg]) => (
                    <li key={field}>
                      <strong>{field}</strong>:{" "}
                      {Array.isArray(msg) ? msg.join(", ") : String(msg)}
                    </li>
                  ))}
                </ul>
              ) : null}
            </Banner>
          ) : null}

          {validation.issues.length ? (
            <Banner $kind="error">
              <div style={{ fontWeight: 700, marginBottom: 6 }}>
                Fix before saving
              </div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {validation.issues.map((i) => (
                  <li key={i}>{i}</li>
                ))}
              </ul>
            </Banner>
          ) : null}

          <Card>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>
              Owner / Relationship (read-only)
            </div>
            <Muted style={{ marginBottom: 10 }}>
              Served by <code>mlist-api</code> (not the hoodie API):{" "}
              <code>
                {isMlistApiConfigured()
                  ? `${MLIST_API_BASE_URL}admin-tools/listings/${id}/owner/`
                  : "REACT_APP_MLIST_API_BASE_URL is not set"}
              </code>
            </Muted>

            {relLoading ? (
              <Muted>
                <em>Loading relationship…</em>
              </Muted>
            ) : relErr ? (
              (() => {
                const msg = relationshipMessage(relErr);
                return (
                  <Banner $kind={msg.kind}>
                    <div style={{ fontWeight: 700 }}>{msg.title}</div>
                    <div>{msg.detail}</div>
                    {relErr?.status ? (
                      <div style={{ marginTop: 6 }}>HTTP {relErr.status}</div>
                    ) : null}
                  </Banner>
                );
              })()
            ) : relationship ? (
              <>
                <div style={{ fontSize: 13, marginBottom: 10 }}>
                  <strong>Listing:</strong> #{relationship?.listing?.id}{" "}
                  {relationship?.listing?.business_name
                    ? `• ${relationship.listing.business_name}`
                    : ""}
                  <span style={{ color: "#999" }}> • </span>
                  <span style={{ color: "#666" }}>
                    source: {relationship?.source || "—"}
                  </span>
                </div>

                {relationship?.owner === null ? (
                  <Banner $kind="warn">
                    <div style={{ fontWeight: 700 }}>
                      No administrator/member linked
                    </div>
                    <div>
                      This listing exists, but{" "}
                      <code>administrator_id</code> is null on the legacy
                      DirectoryEntry row.
                    </div>
                  </Banner>
                ) : (
                  <Grid>
                    <div>
                      <div
                        style={{ fontSize: 12, fontWeight: 700, color: "#444" }}
                      >
                        Administrator / Member
                      </div>
                      <div style={{ fontSize: 13, color: "#666" }}>
                        <div>
                          <strong>member_id:</strong>{" "}
                          {relationship?.owner?.member_id ?? "—"}
                        </div>
                        <div>
                          <strong>is_club_member:</strong>{" "}
                          {String(
                            relationship?.owner?.member?.is_club_member ?? "—"
                          )}
                        </div>
                        <div>
                          <strong>is_list_member:</strong>{" "}
                          {String(
                            relationship?.owner?.member?.is_list_member ?? "—"
                          )}
                        </div>
                        <div>
                          <strong>member.user_id:</strong>{" "}
                          {relationship?.owner?.member?.user_id ?? "—"}
                        </div>
                      </div>
                    </div>

                    <div>
                      <div
                        style={{ fontSize: 12, fontWeight: 700, color: "#444" }}
                      >
                        Django user
                      </div>
                      <div style={{ fontSize: 13, color: "#666" }}>
                        <div>
                          <strong>user.id:</strong>{" "}
                          {relationship?.owner?.user?.id ?? "—"}
                        </div>
                        <div>
                          <strong>email:</strong>{" "}
                          {relationship?.owner?.user?.email ?? "—"}
                        </div>
                        <div>
                          <strong>name:</strong>{" "}
                          {relationship?.owner?.user
                            ? `${relationship.owner.user.first_name || ""} ${
                                relationship.owner.user.last_name || ""
                              }`.trim() || "—"
                            : "—"}
                        </div>
                        <div>
                          <strong>is_active:</strong>{" "}
                          {String(relationship?.owner?.user?.is_active ?? "—")}
                        </div>
                      </div>
                    </div>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <div
                        style={{ fontSize: 12, fontWeight: 700, color: "#444" }}
                      >
                        HoodieProfile (same user, if present)
                      </div>
                      <div style={{ fontSize: 13, color: "#666" }}>
                        {relationship?.owner?.hoodie_profile ? (
                          <>
                            <div>
                              <strong>hoodie_profile.id:</strong>{" "}
                              {relationship.owner.hoodie_profile.id}{" "}
                              <span style={{ color: "#999" }}>•</span>{" "}
                              <Link
                                to={`/admin-tools/hoodies/${relationship.owner.hoodie_profile.id}`}
                              >
                                Open HoodieProfile
                              </Link>
                            </div>
                            <div>
                              <strong>zip_code:</strong>{" "}
                              {relationship.owner.hoodie_profile.zip_code ?? "—"}
                            </div>
                          </>
                        ) : (
                          <Muted>No HoodieProfile found for this user.</Muted>
                        )}
                      </div>
                    </div>
                  </Grid>
                )}
              </>
            ) : (
              <Muted>No relationship data loaded.</Muted>
            )}
          </Card>

          <Card>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>
              Editable fields (production-safe text PATCH)
            </div>
            <Muted style={{ marginBottom: 10 }}>
              Saves only changed fields from{" "}
              <code>DirectoryEntrySerializer</code> scalar text/url/email/phone
              fields. Tags are never sent.
            </Muted>
            <Grid>
              <Label>
                Business name *
                <Input
                  value={form.business_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, business_name: e.target.value }))
                  }
                />
              </Label>
              <Label>
                Contact name
                <Input
                  value={form.contact_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, contact_name: e.target.value }))
                  }
                />
              </Label>
              <Label>
                Contact title
                <Input
                  value={form.contact_title}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, contact_title: e.target.value }))
                  }
                />
              </Label>
              <Label>
                Email
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, email: e.target.value }))
                  }
                />
              </Label>
              <Label>
                Website
                <Input
                  value={form.website}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, website: e.target.value }))
                  }
                />
              </Label>
              <Label>
                Phone
                <Input
                  value={form.phone}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, phone: e.target.value }))
                  }
                />
              </Label>
              <Label>
                Facebook URL
                <Input
                  value={form.facebook_url}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, facebook_url: e.target.value }))
                  }
                />
              </Label>
              <Label>
                Twitter handle
                <Input
                  value={form.twitter_handle}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, twitter_handle: e.target.value }))
                  }
                />
              </Label>
              <Label>
                Instagram username
                <Input
                  value={form.instagram_name}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, instagram_name: e.target.value }))
                  }
                />
              </Label>
              <Label>
                LinkedIn URL
                <Input
                  value={form.linkedin_url}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, linkedin_url: e.target.value }))
                  }
                />
              </Label>

              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#444" }}>
                  Tags (read-only)
                </div>
                <Muted style={{ margin: "4px 0 8px" }}>
                  Tag editing is not available in this tool. PATCH never includes{" "}
                  <code>tags</code>.
                </Muted>
                {tagLabels.length ? (
                  tagLabels.map((label) => (
                    <TagChip key={label}>{label}</TagChip>
                  ))
                ) : (
                  <Muted>No tags on this listing.</Muted>
                )}
              </div>

              <Label style={{ gridColumn: "1 / -1" }}>
                Short description{" "}
                <span style={{ color: "#666" }}>
                  (legacy ~80 chars; model max 100)
                </span>
                <Input
                  value={form.short_description}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      short_description: e.target.value,
                    }))
                  }
                />
                <Muted>
                  Length: {String(form.short_description || "").length}
                  {String(form.short_description || "").length > 80
                    ? " — over legacy 80-char guidance"
                    : ""}
                </Muted>
              </Label>

              <Label style={{ gridColumn: "1 / -1" }}>
                Extended description{" "}
                <span style={{ color: "#666" }}>(max 2000)</span>
                <TextArea
                  value={form.extended_description}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      extended_description: e.target.value,
                    }))
                  }
                />
                <Muted>
                  Length: {String(form.extended_description || "").length} /
                  2000
                </Muted>
              </Label>
            </Grid>

            <div style={{ marginTop: 10, color: "#666", fontSize: 13 }}>
              {textDirty ? (
                <span>
                  Unsaved text changes.
                  {patchKeyCount > 0
                    ? ` PATCH will send: ${Object.keys(pendingPatch).join(", ")}`
                    : " (no diff detected — nothing to save)"}
                </span>
              ) : (
                <span>No text field changes.</span>
              )}
            </div>
          </Card>

          <Card>
            <div style={{ fontWeight: 700, marginBottom: 10 }}>
              Blocked fields (read-only)
            </div>
            <Muted style={{ marginBottom: 10 }}>
              Not sent in PATCH — missing from serializer or not supported for
              write in this tool.
            </Muted>
            <Grid>
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#444" }}>
                  recommended_by
                  <span style={{ fontWeight: 400, color: "#999" }}>
                    {" "}
                    (not on DirectoryEntrySerializer)
                  </span>
                </div>
                <div style={{ fontSize: 13, color: "#666" }}>
                  {blockedFieldDisplay(listing, "recommended_by")}
                </div>
              </div>
              {BLOCKED_ADMIN_FIELDS.filter((f) => f !== "recommended_by").map(
                (f) => (
                  <div key={f}>
                    <div
                      style={{ fontSize: 12, color: "#444", fontWeight: 700 }}
                    >
                      {f}
                      {API_ABSENT_FIELDS.includes(f) ? (
                        <span style={{ fontWeight: 400, color: "#999" }}>
                          {" "}
                          (not on serializer)
                        </span>
                      ) : f === "related_groups" ? (
                        <span style={{ fontWeight: 400, color: "#999" }}>
                          {" "}
                          (read-only on serializer)
                        </span>
                      ) : null}
                    </div>
                    <div style={{ fontSize: 13, color: "#666" }}>
                      {f === "related_groups" && listing?.related_groups
                        ? `${listing.related_groups.length} group(s) in GET`
                        : blockedFieldDisplay(listing, f)}
                    </div>
                  </div>
                )
              )}
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#444" }}>
                  tags
                  <span style={{ fontWeight: 400, color: "#999" }}>
                    {" "}
                    (never PATCHed)
                  </span>
                </div>
                <div style={{ fontSize: 13, color: "#666" }}>
                  Display only — {displayTagIds.length} tag(s)
                </div>
              </div>
            </Grid>
          </Card>

          {devMode ? (
            <Card>
              <div style={{ fontWeight: 700 }}>API diagnostics (read-only)</div>
              <Muted>
                Development only. Observational — no write controls.
              </Muted>

              <div style={{ marginTop: 10, fontSize: 12 }}>
                <strong>Endpoint:</strong> PATCH {HOODIE_API_BASE_URL}
                {detailEndpoint}
              </div>
              <div style={{ marginTop: 6, fontSize: 12 }}>
                <strong>Serializer allowlist (text PATCH):</strong>{" "}
                {PATCH_TEXT_FIELDS.join(", ")}
              </div>
              <div style={{ marginTop: 6, fontSize: 12 }}>
                <strong>Serializer fields (reference):</strong>{" "}
                {SERIALIZER_LISTING_FIELDS.join(", ")}
              </div>
              <div style={{ marginTop: 6, fontSize: 12 }}>
                <strong>GET field keys:</strong>{" "}
                {listingResponseFieldKeys(listing).join(", ") || "—"}
              </div>
              <div style={{ marginTop: 6, fontSize: 12 }}>
                <strong>Tags writable:</strong> no (production policy)
              </div>

              <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700 }}>
                Pending PATCH payload (if Save clicked now)
              </div>
              <DevPanel>
                {patchKeyCount > 0
                  ? JSON.stringify(pendingPatch, null, 2)
                  : "(empty — no text fields changed)"}
              </DevPanel>

              <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700 }}>
                Last PATCH request (observed)
              </div>
              <DevPanel>
                {lastPatchRequest
                  ? JSON.stringify(lastPatchRequest, null, 2)
                  : "(none yet)"}
              </DevPanel>

              <div style={{ marginTop: 10, fontSize: 12, fontWeight: 700 }}>
                Last load / PATCH result (observed)
              </div>
              <DevPanel>
                {lastPatchResult
                  ? JSON.stringify(lastPatchResult, null, 2)
                  : "(none)"}
              </DevPanel>
            </Card>
          ) : null}
        </>
      )}
    </Wrap>
  );
}
